import React, { useState } from 'react';
import { ActivityIndicator, Image as RNImage, Linking, ScrollView, TextInput, TouchableOpacity, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useToast } from 'react-native-toast-notifications';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Modal from '../../../../components/Modal';
import { Icon } from '../../../../components/Icon';
import SignatureView from '../../../../components/SignatureView';
import api from '../../../../utils/api';

dayjs.locale('es');


type Mode = 'view' | 'rejecting' | 'suspending';


export default function AccessAuthorizationDetailModal({ communityId, authorizationUuid, onRequestClose, onChanged }: any) {
  const [mode, setMode] = useState<Mode>('view');
  const [rejectReason, setRejectReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const { data, isLoading, refetch } = useQuery<any>({
    queryKey: ['access-authorizations', authorizationUuid],
    queryFn: () => api.get(`/communities/${communityId}/access-authorizations/${authorizationUuid}`).then(r => r.data),
    enabled: !!authorizationUuid,
  });

  function review(veredict: 'approved' | 'rejected', comments?: string) {
    setIsSubmitting(true);
    api.post(`/communities/${communityId}/access-authorizations/${authorizationUuid}/review`, { veredict, comments })
      .then(() => {
        toast.show(veredict === 'approved' ? 'Autorización avalada' : 'Autorización rechazada');
        onChanged?.();
        refetch();
        if (veredict === 'rejected') setMode('view');
      })
      .catch((err) => alert(err?.response?.data?.message ?? 'Error al revisar'))
      .finally(() => setIsSubmitting(false));
  }

  function onApprove() {
    if (confirm('¿Avalar esta autorización?')) review('approved');
  }

  function onConfirmReject() {
    if (!rejectReason.trim()) {
      alert('El motivo es obligatorio para rechazar');
      return;
    }
    review('rejected', rejectReason.trim());
  }

  function suspend() {
    if (!suspendReason.trim()) {
      alert('El motivo es obligatorio para suspender');
      return;
    }
    setIsSubmitting(true);
    api.post(`/communities/${communityId}/access-authorizations/${authorizationUuid}/suspend`, { reason: suspendReason.trim() })
      .then(() => {
        toast.show('Autorización suspendida');
        onChanged?.();
        refetch();
        setMode('view');
        setSuspendReason('');
      })
      .catch((err) => alert(err?.response?.data?.message ?? 'Error al suspender'))
      .finally(() => setIsSubmitting(false));
  }

  function reactivate() {
    if (!confirm('¿Reactivar esta autorización? El proveedor podrá volver a entrar.')) return;
    setIsSubmitting(true);
    api.post(`/communities/${communityId}/access-authorizations/${authorizationUuid}/reactivate`)
      .then(() => {
        toast.show('Autorización reactivada');
        onChanged?.();
        refetch();
      })
      .catch((err) => alert(err?.response?.data?.message ?? 'Error al reactivar'))
      .finally(() => setIsSubmitting(false));
  }

  const auth = data;
  const isPending = auth?.status === 'pending';
  // Solo se suspende una vigente: una vencida ya no deja entrar
  const canSuspend = auth?.display_status === 'approved';
  const isSuspended = auth?.status === 'suspended';
  const entries: any[] = auth?.entry_logs ?? [];
  const files: any[] = auth?.files ?? [];

  // El contenido se encoge para caber en el alto del modal y hace scroll; las
  // acciones quedan fijas abajo para que el motivo de rechazo/suspensión siempre se vea.
  return (
    <Modal onRequestClose={onRequestClose} style={{ modal: tw`w-[640px] max-w-[90%]`, content: tw`shrink` }}>
      {isLoading && <View style={tw`p-10 items-center`}><ActivityIndicator /></View>}

      {auth && (
        <View style={tw`shrink`}>
          <ScrollView style={tw`shrink`} contentContainerStyle={tw`pr-2`}>
            <Text style={tw`text-secondary font-600 text-lg`}>Detalle de autorización</Text>

            <View style={tw`flex-row justify-between items-center mt-3 mb-3`}>
              <Text style={tw`text-gray-400 text-sm`}>Folio {auth.folio}</Text>
              <View style={tw`flex-row items-center gap-2`}>
                {auth.is_inside && (
                  <View style={tw`px-3 py-1 rounded-full bg-green-100`}>
                    <Text style={tw`text-xs font-600 text-green-700`}>Adentro ahora</Text>
                  </View>
                )}
                <StatusBadge status={auth.display_status ?? auth.status} statusStr={auth.status_str} />
              </View>
            </View>

            {isSuspended && (
              <View style={tw`bg-red-50 border border-red-200 rounded-2xl px-4 py-3 mb-3`}>
                <Text style={tw`text-red-700 font-600`}>Suspendida</Text>
                {auth.suspended_reason && <Text style={tw`text-red-700 mt-1`}>{auth.suspended_reason}</Text>}
                <Text style={tw`text-red-600 text-sm mt-1`}>El proveedor no puede entrar mientras esté suspendida.</Text>
              </View>
            )}

            {/* Card: servicio (tipo del catálogo y/o descripción libre) */}
            {(!!auth.service_type || !!auth.description) && (
              <View style={tw`bg-[#F9FAFB] rounded-2xl border border-gray-200 px-4 py-3 mb-3`}>
                {!!auth.service_type && <Text style={tw`text-secondary font-600 text-base`}>{auth.service_type}</Text>}
                {!!auth.description && (
                  <Text style={tw.style(auth.service_type ? `text-gray-600 mt-1` : `text-secondary font-500 text-base`)}>{auth.description}</Text>
                )}
              </View>
            )}

            {/* Card: detalles listados */}
            <View style={tw`bg-[#F9FAFB] rounded-2xl border border-gray-200 px-4 py-3 gap-y-2 mb-3`}>
              <Row label='Vivienda' value={auth.unit?.name} />
              <Row label='Solicita' value={auth.requester?.name} />
              <Row label='Tipo de acceso' value={auth.access_type_str} />
              {['pending', 'approved', 'suspended'].includes(auth.status) ? (
                <DatesRow
                  auth={auth}
                  communityId={communityId}
                  onSaved={() => { refetch(); onChanged?.(); }}
                />
              ) : (
                <Row label='Fechas' value={formatRange(auth)} />
              )}
              <Row label='Horario' value={formatTime(auth)} />
              {auth.provider_name && <Row label='Proveedor' value={auth.provider_name} />}
            </View>

            {files.length > 0 && (
              <View style={tw`mb-3`}>
                <Text style={tw`text-gray-500 font-500 mb-2`}>Adjuntos del residente</Text>
                <View style={tw`flex-row flex-wrap gap-2`}>
                  {files.map((f: any) => <FileThumb key={f.uuid} file={f} />)}
                </View>
              </View>
            )}

            <SignatureView xml={auth.signature} />

            {(auth.status === 'approved' || isSuspended) && (
              <EntriesHistory entries={entries} />
            )}

            {auth.reviewed_at && (
              <View style={tw`mt-4 pt-3 border-t border-gray-100`}>
                <Text style={tw`text-gray-500 text-sm`}>
                  {auth.status === 'approved' ? 'Avalada' : auth.status === 'rejected' ? 'Rechazada' : 'Revisada'} el{' '}
                  <Text style={tw`text-gray-700 font-500`}>{dayjs(auth.reviewed_at).format('DD/MM/YY HH:mm')}</Text>
                  {auth.reviewer?.name && <> por <Text style={tw`text-gray-700 font-500`}>{auth.reviewer.name}</Text></>}
                </Text>
                {auth.review_comments && (
                  <Text style={tw`text-red-500 mt-2`}>{auth.review_comments}</Text>
                )}
              </View>
            )}

          </ScrollView>

          {/* Acciones */}
          {isPending && mode === 'view' && (
            <View style={tw`flex-row justify-end gap-3 mt-6`}>
              <TouchableOpacity
                disabled={isSubmitting}
                onPress={() => setMode('rejecting')}
                style={tw.style(`flex-row items-center px-5 py-2.5 rounded-full bg-red`, isSubmitting && `opacity-60`)}
              >
                <Icon name="rechazar" variant="white" size={16} style={tw`mr-1.5`} />
                <Text style={tw`text-white font-500`}>Rechazar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                disabled={isSubmitting}
                onPress={onApprove}
                style={tw.style(`flex-row items-center px-5 py-2.5 rounded-full bg-primary`, isSubmitting && `opacity-60`)}
              >
                <Icon name="valido" variant="white" size={16} style={tw`mr-1.5`} />
                <Text style={tw`text-white font-500`}>Avalar</Text>
              </TouchableOpacity>
            </View>
          )}

          {(canSuspend || isSuspended) && mode === 'view' && (
            <View style={tw`flex-row justify-end gap-3 mt-6`}>
              {canSuspend && (
                <TouchableOpacity
                  disabled={isSubmitting}
                  onPress={() => setMode('suspending')}
                  style={tw.style(`px-5 py-2.5 rounded-full border border-red`, isSubmitting && `opacity-60`)}
                >
                  <Text style={tw`text-red font-500`}>Suspender</Text>
                </TouchableOpacity>
              )}
              {isSuspended && (
                <TouchableOpacity
                  disabled={isSubmitting}
                  onPress={reactivate}
                  style={tw.style(`px-5 py-2.5 rounded-full bg-primary`, isSubmitting && `opacity-60`)}
                >
                  <Text style={tw`text-white font-500`}>Reactivar</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {mode === 'suspending' && (
            <View style={tw`mt-6 p-4 bg-red-50 border border-red-200 rounded-xl`}>
              <Text style={tw`text-red-700 font-600 mb-1`}>Motivo de la suspensión</Text>
              <Text style={tw`text-red-600 text-sm mb-2`}>
                Lo verá el vigilante si el proveedor se presenta. Si ya está adentro, todavía se le puede registrar la salida.
              </Text>
              <TextInput
                value={suspendReason}
                onChangeText={setSuspendReason}
                multiline
                numberOfLines={3}
                maxLength={500}
                placeholder='Ej. Trabajos fuera del horario permitido'
                style={tw`bg-white border border-gray-200 rounded-lg p-3 mb-3 text-sm`}
              />
              <View style={tw`flex-row justify-end gap-3`}>
                <TouchableOpacity
                  disabled={isSubmitting}
                  onPress={() => { setMode('view'); setSuspendReason(''); }}
                  style={tw`px-5 py-2.5 rounded-full border border-gray-300`}
                >
                  <Text style={tw`text-gray-600 font-500`}>Cancelar</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  disabled={isSubmitting || !suspendReason.trim()}
                  onPress={suspend}
                  style={tw.style(`px-5 py-2.5 rounded-full bg-red`, (!suspendReason.trim() || isSubmitting) && `opacity-60`)}
                >
                  <Text style={tw`text-white font-500`}>Confirmar suspensión</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {isPending && mode === 'rejecting' && (
            <View style={tw`mt-6 p-4 bg-red-50 border border-red-200 rounded-xl`}>
              <Text style={tw`text-red-700 font-600 mb-2`}>Motivo del rechazo</Text>
              <TextInput
                value={rejectReason}
                onChangeText={setRejectReason}
                multiline
                numberOfLines={3}
                placeholder='Explica al residente por qué se rechaza…'
                style={tw`bg-white border border-gray-200 rounded-lg p-3 mb-3 text-sm`}
              />
              <View style={tw`flex-row justify-end gap-3`}>
                <TouchableOpacity
                  disabled={isSubmitting}
                  onPress={() => { setMode('view'); setRejectReason(''); }}
                  style={tw`px-5 py-2.5 rounded-full border border-gray-300`}
                >
                  <Text style={tw`text-gray-600 font-500`}>Cancelar</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  disabled={isSubmitting || !rejectReason.trim()}
                  onPress={onConfirmReject}
                  style={tw.style(`px-5 py-2.5 rounded-full bg-red`, (!rejectReason.trim() || isSubmitting) && `opacity-60`)}
                >
                  <Text style={tw`text-white font-500`}>Confirmar rechazo</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      )}
    </Modal>
  );
}


/** Fechas con edición en línea (input date del navegador). Sin fecha final es de un solo día. */
function DatesRow({ auth, communityId, onSaved }: any) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  function startEditing() {
    setStart(dayjs(auth.start_date).format('YYYY-MM-DD'));
    setEnd(auth.end_date ? dayjs(auth.end_date).format('YYYY-MM-DD') : '');
    setError(null);
    setEditing(true);
  }

  function save() {
    if (!start) return setError('La fecha de inicio es obligatoria');
    if (end && end < start) return setError('La fecha final no puede ser antes del inicio');

    setIsSaving(true);
    api.patch(`/communities/${communityId}/access-authorizations/${auth.uuid}`, {
      start_date: start,
      end_date: end && end !== start ? end : null,
    })
      .then(() => {
        toast.show('Fechas actualizadas');
        setEditing(false);
        onSaved?.();
      })
      .catch((err) => setError(err?.response?.data?.message ?? 'No se pudieron guardar las fechas'))
      .finally(() => setIsSaving(false));
  }

  if (!editing) {
    return (
      <View style={tw`flex-row items-center`}>
        <Text style={tw`text-gray-500 w-32`}>Fechas</Text>
        <Text style={tw`text-gray-800 flex-1`}>{formatRange(auth)}</Text>
        <TouchableOpacity onPress={startEditing}>
          <Text style={tw`text-primary font-500 text-sm`}>Editar</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const inputStyle = { border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 10px', fontSize: 14, fontFamily: 'Poppins_400Regular' };

  return (
    <View style={tw`py-1`}>
      <Text style={tw`text-gray-500 mb-2`}>Fechas</Text>
      <View style={tw`flex-row items-center flex-wrap gap-2`}>
        <input type="date" value={start} onChange={e => setStart(e.target.value)} style={inputStyle} />
        <Text style={tw`text-gray-400`}>al</Text>
        <input type="date" value={end} min={start} onChange={e => setEnd(e.target.value)} style={inputStyle} />
        {!!end && (
          <TouchableOpacity onPress={() => setEnd('')}>
            <Text style={tw`text-gray-500 text-sm`}>Un solo día</Text>
          </TouchableOpacity>
        )}
      </View>
      <Text style={tw`text-gray-400 text-xs mt-1`}>Si ya está avalada, el QR del proveedor toma las fechas nuevas.</Text>
      {!!error && <Text style={tw`text-red-500 text-sm mt-1`}>{error}</Text>}
      <View style={tw`flex-row justify-end gap-2 mt-2`}>
        <TouchableOpacity onPress={() => setEditing(false)} style={tw`px-4 py-1.5 rounded-full border border-gray-300`}>
          <Text style={tw`text-gray-600 text-sm font-500`}>Cancelar</Text>
        </TouchableOpacity>
        <TouchableOpacity
          disabled={isSaving}
          onPress={save}
          style={tw.style(`px-4 py-1.5 rounded-full bg-primary`, isSaving && `opacity-60`)}
        >
          <Text style={tw`text-white text-sm font-500`}>{isSaving ? 'Guardando…' : 'Guardar'}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}


function Row({ label, value }: any) {
  return (
    <View style={tw`flex-row`}>
      <Text style={tw`text-gray-500 w-32`}>{label}</Text>
      <Text style={tw`text-gray-800 flex-1`}>{value}</Text>
    </View>
  );
}


function StatusBadge({ status, statusStr }: any) {
  // Colores oficiales: pendiente ámbar, avalada turquesa, rechazada rojo DingDong.
  const map: Record<string, { bg: string; text: string }> = {
    pending:   { bg: 'bg-amber-100',   text: 'text-amber-600' },
    approved:  { bg: 'bg-primary-100', text: 'text-primary' },
    rejected:  { bg: 'bg-red/10',      text: 'text-red' },
    suspended: { bg: 'bg-red/10',      text: 'text-red' },
    cancelled: { bg: 'bg-gray-200',    text: 'text-gray-600' },
    expired:   { bg: 'bg-gray-200',    text: 'text-gray-600' },
  };
  const s = map[status as string] || map.cancelled;

  return (
    <View style={tw.style(`px-3 py-1 rounded-full`, s.bg)}>
      <Text style={tw.style(`text-xs font-600`, s.text)}>{statusStr}</Text>
    </View>
  );
}


/** Ingresos registrados en caseta o tótem, con su evidencia. */
function EntriesHistory({ entries }: { entries: any[] }) {
  const [signatureOf, setSignatureOf] = useState<string | null>(null);

  return (
    <View style={tw`mt-4`}>
      <Text style={tw`text-gray-500 font-500 mb-2`}>Ingresos ({entries.length})</Text>

      {entries.length === 0 && (
        <Text style={tw`text-gray-400 text-sm`}>Todavía no hay ingresos registrados.</Text>
      )}

      {entries.map((log: any) => {
        const entry = log.entry_time ? dayjs(log.entry_time) : null;
        const exit = log.exit_time ? dayjs(log.exit_time) : null;
        const day = (entry ?? exit)!;
        const photos: any[] = log.images ?? [];
        const extra = log.extra ?? {};

        return (
          <View key={log.uuid} style={tw`bg-[#F9FAFB] rounded-2xl border border-gray-200 px-4 py-3 mb-2`}>
            <View style={tw`flex-row justify-between items-center`}>
              <Text style={tw`text-secondary font-500`}>{day.format('ddd D MMM')}</Text>
              <Text style={tw`text-gray-700`}>
                {entry ? entry.format('HH:mm') : '—'} → {exit ? (exit.isSame(entry ?? exit, 'day') ? exit.format('HH:mm') : exit.format('D MMM HH:mm')) : (entry ? 'Adentro' : '—')}
              </Text>
            </View>

            <Text style={tw`text-gray-500 text-sm mt-1`}>
              {[
                log.type === 'entry' ? 'Tótem' : log.type === 'exit' ? 'Solo salida' : log.user?.name ? `Registró ${log.user.name}` : null,
                extra.people_count ? `${extra.people_count} ${extra.people_count === 1 ? 'persona' : 'personas'}` : null,
                log.vehicle_plate_number ? `Placas ${log.vehicle_plate_number}` : null,
                extra.outside_service_hours ? 'Fuera de horario' : null,
              ].filter(Boolean).join(' · ')}
            </Text>

            {!!log.comments && <Text style={tw`text-gray-600 text-sm mt-1`}>{log.comments}</Text>}

            {(photos.length > 0 || extra.signature) && (
              <View style={tw`flex-row flex-wrap gap-2 mt-2`}>
                {photos.map((img: any) => (
                  <FileThumb key={img.uuid} file={img} />
                ))}
                {extra.signature && (
                  <TouchableOpacity
                    onPress={() => setSignatureOf(signatureOf === log.uuid ? null : log.uuid)}
                    style={tw`w-16 h-16 rounded-lg border border-gray-200 bg-white items-center justify-center`}
                  >
                    <Text style={tw`text-primary text-xs font-500`}>Firma</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}

            {signatureOf === log.uuid && <SignatureView xml={extra.signature} label='Firma del proveedor' />}
          </View>
        );
      })}
    </View>
  );
}


function FileThumb({ file, label }: { file: any; label?: string }) {
  const isImage = file.file_type !== 'pdf';
  const thumb = file.thumbnails?.['250']?.url ?? file.url;

  return (
    <TouchableOpacity onPress={() => Linking.openURL(file.url)} style={tw`items-center`}>
      {isImage ? (
        <RNImage source={{ uri: thumb }} style={tw`w-16 h-16 rounded-lg bg-gray-100`} />
      ) : (
        <View style={tw`w-16 h-16 rounded-lg border border-gray-200 bg-white items-center justify-center`}>
          <Text style={tw`text-gray-500 text-xs font-600`}>PDF</Text>
        </View>
      )}
      {!!label && <Text style={tw`text-gray-500 text-xs mt-0.5`}>{label}</Text>}
    </TouchableOpacity>
  );
}


function formatRange(auth: any) {
  const start = dayjs(auth.start_date);
  const end = auth.end_date ? dayjs(auth.end_date) : null;
  if (!end || start.isSame(end, 'day')) return start.format('ddd D MMM YYYY');
  return `${start.format('D MMM')} — ${end.format('D MMM YYYY')}`;
}

function formatTime(auth: any) {
  if (!auth.start_time) return 'Todo el día';
  const start = auth.start_time.slice(0, 5);
  const end = auth.end_time ? auth.end_time.slice(0, 5) : null;
  return end ? `${start} - ${end}` : `Desde ${start}`;
}
